import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";
import { List, Loader2, Plus, Minus } from "lucide-react";

interface CreateRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (ruleData: any) => Promise<void>;
  editingRule?: any;
}

export function CreateRuleModal({
  isOpen,
  onClose,
  onSave,
  editingRule,
}: CreateRuleModalProps) {
  const [formData, setFormData] = useState({
    rule_name: "",
    description: "",
    price_modifier: "",
    price_type: "fixed",
    is_required: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [operation, setOperation] = useState<"add" | "subtract">("add");

  useEffect(() => {
    if (isOpen && editingRule) {
      const modifier = parseFloat(editingRule.price_modifier.toString());
      setOperation(modifier < 0 ? "subtract" : "add");
      setFormData({
        rule_name: editingRule.rule_name,
        description: editingRule.description || "",
        price_modifier: Math.abs(modifier).toString(),
        price_type: editingRule.price_type,
        is_required: editingRule.is_required,
      });
    } else if (isOpen && !editingRule) {
      resetForm();
    }
  }, [isOpen, editingRule]);

  const resetForm = () => {
    setFormData({
      rule_name: "",
      description: "",
      price_modifier: "",
      price_type: "fixed",
      is_required: false,
    });
    setOperation("add");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.rule_name.trim()) {
      newErrors.rule_name = "El nombre es obligatorio";
    }

    if (!formData.price_modifier) {
      newErrors.price_modifier = "El modificador de precio es obligatorio";
    } else if (isNaN(parseFloat(formData.price_modifier))) {
      newErrors.price_modifier = "Debe ser un número válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      let modifierValue = parseFloat(formData.price_modifier);
      if (operation === "subtract") {
        modifierValue = -Math.abs(modifierValue);
      } else {
        modifierValue = Math.abs(modifierValue);
      }

      const payload: any = {
        ...formData,
        price_modifier: modifierValue,
      };

      if (!editingRule) {
        payload.order_position = 0;
      }

      await onSave(payload);
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <List className="h-5 w-5 text-blue-600" />
            {editingRule ? "Editar Ajuste de Precio" : "Nuevo Ajuste de Precio"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="rule_name">Nombre del Ajuste *</Label>
            <Input
              id="rule_name"
              value={formData.rule_name}
              onChange={(e) => handleChange("rule_name", e.target.value)}
              placeholder="Ej: Descuento por volumen"
            />
            {errors.rule_name && (
              <p className="text-sm text-red-500">{errors.rule_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Tipo de Ajuste</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={operation === "add" ? "default" : "outline"}
                className={`flex-1 ${
                  operation === "add"
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "hover:bg-green-50 text-green-700 border-green-200"
                }`}
                onClick={() => setOperation("add")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Aumento (+)
              </Button>
              <Button
                type="button"
                variant={operation === "subtract" ? "default" : "outline"}
                className={`flex-1 ${
                  operation === "subtract"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "hover:bg-red-50 text-red-700 border-red-200"
                }`}
                onClick={() => setOperation("subtract")}
              >
                <Minus className="h-4 w-4 mr-2" />
                Descuento (-)
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price_modifier">Valor *</Label>
              <Input
                id="price_modifier"
                type="number"
                step="0.01"
                min="0"
                value={formData.price_modifier}
                onChange={(e) => handleChange("price_modifier", e.target.value)}
                placeholder="0.00"
              />
              {errors.price_modifier && (
                <p className="text-sm text-red-500">{errors.price_modifier}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_type">Tipo</Label>
              <Select
                value={formData.price_type}
                onValueChange={(value) => handleChange("price_type", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fijo ($)</SelectItem>
                  <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between border p-3 rounded-md">
            <div className="space-y-0.5">
              <Label htmlFor="is_required">Ajuste Obligatorio</Label>
              <p className="text-xs text-slate-500">
                Se aplicará automáticamente
              </p>
            </div>
            <Switch
              id="is_required"
              checked={formData.is_required}
              onCheckedChange={(checked) =>
                handleChange("is_required", checked)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Descripción opcional..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingRule ? "Guardar Cambios" : "Crear Ajuste"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
